<template>
  <AppCard variant="outlined" class="overflow-hidden transition-shadow" :class="composing ? 'shadow-md' : 'hover:shadow-sm'">
    <!-- Collapsed state -->
    <button
      v-if="!composing"
      class="w-full p-4 flex items-center gap-3 text-left group"
      @click="startCompose"
    >
      <div
        class="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
        :class="isSchoolWide
          ? 'bg-gradient-to-br from-blue-500 to-indigo-600'
          : 'bg-gradient-to-br from-emerald-400 to-teal-500'"
      >
        <font-awesome-icon :icon="isSchoolWide ? ['fas', 'tower-broadcast'] : ['fas', 'pen']" class="text-sm text-white" />
      </div>
      <div class="flex-1 py-2 px-3 bg-gray-50 rounded-lg group-hover:bg-gray-100 transition-colors">
        <span class="text-gray-400 group-hover:text-gray-500 text-sm">
          {{ isSchoolWide ? 'Broadcast to the whole school...' : 'Share something with your class...' }}
        </span>
      </div>
    </button>

    <!-- Expanded state -->
    <div v-else>
      <!-- School-wide banner -->
      <div v-if="isSchoolWide" class="px-5 pt-4 pb-3 bg-indigo-50/60 border-b border-indigo-100">
        <div class="flex items-center gap-2 text-indigo-700">
          <font-awesome-icon :icon="['fas', 'tower-broadcast']" class="text-sm" />
          <span class="text-sm font-semibold">School-wide post</span>
        </div>
        <p class="text-xs text-indigo-500 mt-0.5">This will be visible to everyone in your school</p>
      </div>

      <!-- Top bar: type pills -->
      <div class="px-5 pt-4 pb-3 border-b border-gray-100">
        <div class="flex items-center gap-1.5 flex-wrap">
          <span class="text-xs text-gray-400 mr-1">Tag as</span>
          <button
            v-for="tp in typePills"
            :key="tp.type"
            class="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors"
            :class="form.type === tp.type ? tp.activeClass : 'border-gray-200 text-gray-500 hover:bg-gray-50'"
            @click="toggleType(tp.type)"
          >
            <font-awesome-icon :icon="tp.icon" class="text-[10px]" />
            {{ tp.label }}
          </button>
        </div>
      </div>

      <!-- Writing area -->
      <div class="px-5 pt-3 pb-2">
        <input
          v-model="form.title"
          type="text"
          placeholder="Title (optional)"
          class="w-full text-base font-semibold text-gray-900 placeholder-gray-300 border-0 focus:outline-none focus:ring-0 p-0 mb-2"
        />
        <textarea
          ref="textareaRef"
          v-model="form.body"
          placeholder="Write something..."
          rows="4"
          maxlength="5000"
          class="w-full text-sm text-gray-700 placeholder-gray-400 border-0 focus:outline-none focus:ring-0 p-0 resize-none leading-relaxed"
        />
        <p v-if="form.body.length > 4500" class="text-xs text-gray-400 text-right mt-1">
          {{ form.body.length }} / 5,000
        </p>

        <!-- Photo previews -->
        <div v-if="photos.length" class="flex flex-wrap gap-2 mt-2">
          <div
            v-for="(photo, i) in photos"
            :key="i"
            class="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200"
          >
            <img :src="photo.preview" class="w-full h-full object-cover" />
            <button
              class="absolute top-0.5 right-0.5 w-5 h-5 bg-black/60 text-white rounded-full flex items-center justify-center text-xs hover:bg-black/80"
              @click="removePhoto(i)"
            >
              <font-awesome-icon :icon="['fas', 'xmark']" class="text-[9px]" />
            </button>
          </div>
        </div>

        <!-- Event fields (only when EVENT type selected) -->
        <div v-if="form.type === 'EVENT'" class="mt-3 p-3 rounded-lg bg-purple-50/50 border border-purple-100/60 space-y-2.5">
          <div class="flex items-center gap-2">
            <input
              v-model="form.eventDate"
              type="date"
              placeholder="Date"
              class="flex-1 px-2.5 py-1.5 bg-white border border-purple-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-purple-400 focus:border-transparent"
            />
            <label class="flex items-center gap-1.5 text-xs text-purple-700 font-medium cursor-pointer shrink-0">
              <input v-model="form.eventAllDay" type="checkbox" class="rounded border-purple-300 text-purple-600 focus:ring-purple-500 w-3.5 h-3.5" />
              All day
            </label>
          </div>
          <div v-if="!form.eventAllDay" class="flex items-center gap-2">
            <input
              v-model="form.eventStartTime"
              type="time"
              placeholder="Start"
              class="flex-1 px-2.5 py-1.5 bg-white border border-purple-200 rounded-lg text-sm text-gray-800 text-center focus:outline-none focus:ring-1 focus:ring-purple-400 focus:border-transparent"
            />
            <span class="text-gray-300 text-xs">-</span>
            <input
              v-model="form.eventEndTime"
              type="time"
              placeholder="End"
              class="flex-1 px-2.5 py-1.5 bg-white border border-purple-200 rounded-lg text-sm text-gray-800 text-center focus:outline-none focus:ring-1 focus:ring-purple-400 focus:border-transparent"
            />
          </div>
          <input
            v-model="form.eventLocation"
            type="text"
            placeholder="Location (optional)"
            class="w-full px-2.5 py-1.5 bg-white border border-purple-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-300 focus:outline-none focus:ring-1 focus:ring-purple-400 focus:border-transparent"
          />
        </div>

        <!-- Attachments row: photos + tag people -->
        <div class="flex items-center gap-2 mt-2">
          <input
            ref="fileInputRef"
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif"
            multiple
            class="hidden"
            @change="onFilesSelected"
          />
          <button
            class="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors"
            :class="photos.length
              ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
              : 'border-gray-200 text-gray-500 hover:bg-gray-50'"
            @click="fileInputRef?.click()"
          >
            <font-awesome-icon :icon="['fas', 'image']" class="text-[10px]" />
            {{ photos.length ? `${photos.length} photo${photos.length > 1 ? 's' : ''}` : 'Photos' }}
          </button>

          <!-- Tag people button (space posts only) -->
          <button
            v-if="!isSchoolWide && members.length"
            class="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors"
            :class="taggedStudentIds.length
              ? 'border-violet-300 bg-violet-50 text-violet-700'
              : 'border-gray-200 text-gray-500 hover:bg-gray-50'"
            @click="showTagPicker = !showTagPicker"
          >
            <font-awesome-icon :icon="['fas', 'user-tag']" class="text-[10px]" />
            {{ taggedStudentIds.length ? `${taggedStudentIds.length} tagged` : 'Tag people' }}
          </button>
        </div>
      </div>

      <!-- Tag picker panel -->
      <div v-if="showTagPicker && !isSchoolWide" class="px-5 py-3 border-t border-gray-100 bg-gray-50/50">
        <div class="flex items-center justify-between mb-2">
          <p class="text-xs font-medium text-gray-500">Tag students</p>
          <!-- Visibility toggle (only shows when people are tagged) -->
          <div v-if="taggedStudentIds.length" class="flex items-center gap-1.5">
            <span class="text-[10px] text-gray-400">Visible to:</span>
            <button
              class="text-[10px] font-medium px-2 py-0.5 rounded-full border transition-colors"
              :class="form.visibility === 'TAGGED'
                ? 'bg-violet-100 text-violet-700 border-violet-300'
                : 'bg-gray-100 text-gray-600 border-gray-200'"
              @click="form.visibility = form.visibility === 'TAGGED' ? 'EVERYONE' : 'TAGGED'"
            >
              {{ form.visibility === 'TAGGED' ? 'Tagged only' : 'Everyone' }}
            </button>
          </div>
        </div>
        <!-- Search -->
        <input
          v-model="tagSearch"
          type="text"
          placeholder="Search students..."
          class="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-transparent mb-2"
        />
        <!-- Student list (max 5 shown, scrollable) -->
        <div class="max-h-[160px] overflow-y-auto space-y-0.5">
          <label
            v-for="student in filteredStudents"
            :key="student.studentId"
            class="flex items-center gap-2.5 px-2 py-1.5 rounded-lg cursor-pointer transition-colors"
            :class="taggedStudentIds.includes(student.studentId!)
              ? 'bg-violet-50'
              : 'hover:bg-gray-100'"
          >
            <input
              type="checkbox"
              :checked="taggedStudentIds.includes(student.studentId!)"
              class="rounded border-gray-300 text-violet-600 focus:ring-violet-500 w-3.5 h-3.5"
              @change="toggleTag(student.studentId!)"
            />
            <span class="text-sm text-gray-700">{{ student.name }}</span>
          </label>
          <p v-if="!filteredStudents.length" class="text-xs text-gray-400 py-2 text-center">
            {{ tagSearch ? 'No matching students' : 'No students in this space' }}
          </p>
        </div>
      </div>

      <!-- Post as (teachers only) -->
      <div v-if="isTeacher" class="px-5 py-3 border-t border-gray-100">
        <p class="text-xs font-medium text-gray-500 mb-2">Post as</p>
        <div class="space-y-1.5">
          <label
            class="flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors"
            :class="form.postAs === 'SPACE'
              ? (isSchoolWide ? 'border-indigo-300 bg-indigo-50/60' : 'border-emerald-300 bg-emerald-50/60')
              : 'border-gray-200 hover:bg-gray-50'"
          >
            <input v-model="form.postAs" type="radio" value="SPACE" class="hidden" />
            <div
              class="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
              :class="form.postAs === 'SPACE'
                ? (isSchoolWide ? 'bg-indigo-200 text-indigo-700' : 'bg-emerald-200 text-emerald-700')
                : 'bg-gray-100 text-gray-400'"
            >
              <font-awesome-icon :icon="isSchoolWide ? ['fas', 'tower-broadcast'] : ['fas', 'users-rectangle']" class="text-xs" />
            </div>
            <div class="min-w-0">
              <p class="text-sm font-medium text-gray-900">{{ isSchoolWide ? 'The School' : (activeSpaceName || 'The Class') }}</p>
              <p class="text-xs text-gray-400">{{ isSchoolWide ? 'Post without your name' : 'Appears as the class name' }}</p>
            </div>
            <font-awesome-icon v-if="form.postAs === 'SPACE'" :icon="['fas', 'circle-check']" class="ml-auto text-sm shrink-0" :class="isSchoolWide ? 'text-indigo-500' : 'text-emerald-500'" />
          </label>
          <label
            class="flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors"
            :class="form.postAs === 'TEACHER'
              ? 'border-indigo-300 bg-indigo-50/60'
              : 'border-gray-200 hover:bg-gray-50'"
          >
            <input v-model="form.postAs" type="radio" value="TEACHER" class="hidden" />
            <div
              class="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
              :class="form.postAs === 'TEACHER' ? 'bg-indigo-200 text-indigo-700' : 'bg-gray-100 text-gray-400'"
            >
              <font-awesome-icon :icon="['fas', 'user']" class="text-xs" />
            </div>
            <div class="min-w-0">
              <p class="text-sm font-medium text-gray-900">{{ teacherName }}</p>
              <p class="text-xs text-gray-400">Shows your name</p>
            </div>
            <font-awesome-icon v-if="form.postAs === 'TEACHER'" :icon="['fas', 'circle-check']" class="ml-auto text-indigo-500 text-sm shrink-0" />
          </label>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50/50">
        <p v-if="error" class="text-xs text-red-600">{{ error }}</p>
        <div v-else />
        <div class="flex items-center gap-2">
          <AppButton variant="ghost" size="sm" @click="cancelCompose">
            Cancel
          </AppButton>
          <AppButton
            variant="primary"
            size="sm"
            :disabled="!canSubmit"
            :loading="submitting"
            @click="submitPost"
          >
            {{ isSchoolWide ? 'Broadcast' : 'Post' }}
          </AppButton>
        </div>
      </div>
    </div>
  </AppCard>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { AppButton, AppCard } from '@/design-system/components'
import { useAnalytics } from '@/composables/useAnalytics'
import { connectApi, eventsApi } from '@/api'
import type { SpaceSummary, SpaceMember, PostType, PostVisibility } from '@/api'

const props = defineProps<{
  spaces: SpaceSummary[]
  activeSpaceId?: string
  activeSpaceName?: string
  teacherName?: string
  isTeacher: boolean
  members: SpaceMember[]
}>()

const emit = defineEmits<{
  posted: []
}>()

const { capture } = useAnalytics()
const isSchoolWide = computed(() => !props.activeSpaceId)

const composing = ref(false)
const submitting = ref(false)
const error = ref<string | null>(null)
const textareaRef = ref<HTMLTextAreaElement | null>(null)
const fileInputRef = ref<HTMLInputElement | null>(null)
const showTagPicker = ref(false)
const tagSearch = ref('')

const form = ref({
  body: '',
  title: '',
  postAs: 'SPACE' as 'SPACE' | 'TEACHER',
  type: null as PostType | null,
  visibility: 'EVERYONE' as PostVisibility,
  eventDate: '',
  eventStartTime: '',
  eventEndTime: '',
  eventAllDay: false,
  eventLocation: '',
})

const taggedStudentIds = ref<string[]>([])
const photos = ref<{ file: File; preview: string }[]>([])

const typePills = [
  { type: 'ANNOUNCEMENT' as PostType, label: 'Announcement', icon: ['fas', 'bullhorn'], activeClass: 'bg-blue-50 text-blue-700 border-blue-300' },
  { type: 'REMINDER' as PostType, label: 'Reminder', icon: ['fas', 'bell'], activeClass: 'bg-amber-50 text-amber-700 border-amber-300' },
  { type: 'MOMENT' as PostType, label: 'Moment', icon: ['fas', 'camera'], activeClass: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
  { type: 'EVENT' as PostType, label: 'Event', icon: ['fas', 'calendar-day'], activeClass: 'bg-purple-100 text-purple-700 ring-purple-300' },
]

const studentMembers = computed(() =>
  props.members.filter(m => m.role === 'STUDENT' && m.studentId),
)

const filteredStudents = computed(() => {
  if (!tagSearch.value.trim()) return studentMembers.value
  const q = tagSearch.value.toLowerCase()
  return studentMembers.value.filter(m => m.name.toLowerCase().includes(q))
})

const canSubmit = computed(() => {
  if (!form.value.body.trim()) return false
  if (isSchoolWide.value) return true
  if (props.activeSpaceId) return true
  return false
})

function startCompose() {
  composing.value = true
  nextTick(() => textareaRef.value?.focus())
}

function cancelCompose() {
  composing.value = false
  form.value = { body: '', title: '', postAs: 'SPACE', type: null, visibility: 'EVERYONE', eventDate: '', eventStartTime: '', eventEndTime: '', eventAllDay: false, eventLocation: '' }
  taggedStudentIds.value = []
  photos.value = []
  showTagPicker.value = false
  tagSearch.value = ''
  error.value = null
}

function toggleType(type: PostType) {
  form.value.type = form.value.type === type ? null : type
}

function toggleTag(studentId: string) {
  const idx = taggedStudentIds.value.indexOf(studentId)
  if (idx >= 0) {
    taggedStudentIds.value.splice(idx, 1)
  } else {
    taggedStudentIds.value.push(studentId)
  }
  // Auto-set visibility when first person tagged
  if (taggedStudentIds.value.length > 0 && form.value.visibility === 'EVERYONE') {
    form.value.visibility = 'TAGGED'
  }
  if (taggedStudentIds.value.length === 0) {
    form.value.visibility = 'EVERYONE'
  }
}

function removePhoto(index: number) {
  URL.revokeObjectURL(photos.value[index]!.preview)
  photos.value.splice(index, 1)
}

const MAX_PHOTOS = 10
const MAX_PHOTO_SIZE = 10 * 1024 * 1024 // 10 MB

function onFilesSelected(e: Event) {
  const input = e.target as HTMLInputElement
  const files = input.files
  if (!files) return

  for (const file of Array.from(files)) {
    if (photos.value.length >= MAX_PHOTOS) break
    if (file.size > MAX_PHOTO_SIZE) {
      error.value = `${file.name} is too large (max 10 MB)`
      continue
    }
    photos.value.push({ file, preview: URL.createObjectURL(file) })
  }
  input.value = '' // reset so same file can be re-selected
}

async function submitPost() {
  if (!canSubmit.value || submitting.value) return
  submitting.value = true
  error.value = null

  try {
    if (form.value.type === 'EVENT' && form.value.eventDate) {
      const startTime = form.value.eventAllDay
        ? `${form.value.eventDate}T00:00:00`
        : form.value.eventStartTime
          ? `${form.value.eventDate}T${form.value.eventStartTime}:00`
          : `${form.value.eventDate}T00:00:00`
      const endTime = !form.value.eventAllDay && form.value.eventEndTime
        ? `${form.value.eventDate}T${form.value.eventEndTime}:00`
        : undefined
      await eventsApi.createEvent({
        title: form.value.title.trim() || form.value.body.trim(),
        description: form.value.body.trim() || undefined,
        startTime,
        endTime,
        allDay: form.value.eventAllDay || undefined,
        location: form.value.eventLocation.trim() || undefined,
        spaceId: props.activeSpaceId || undefined,
      })
    } else {
      const data = {
        type: (form.value.type ?? 'MESSAGE') as PostType,
        title: form.value.title.trim() || undefined,
        body: form.value.body.trim(),
        showAuthor: form.value.postAs === 'TEACHER',
        ...(taggedStudentIds.value.length ? {
          taggedStudentIds: taggedStudentIds.value,
          visibility: form.value.visibility,
        } : {}),
      }

      let createdPost
      if (isSchoolWide.value) {
        createdPost = await connectApi.createSchoolWidePost(data)
      } else if (props.activeSpaceId) {
        createdPost = await connectApi.createPost(props.activeSpaceId, data)
      }

      // Upload photos after post creation
      if (createdPost && photos.value.length) {
        for (const photo of photos.value) {
          try {
            await connectApi.uploadAttachment(createdPost.id, photo.file, createdPost.spaceId)
          } catch (err) {
            console.warn('Failed to upload photo:', err)
          }
        }
      }
    }

    capture('connect_post_created', { post_type: (form.value.type ?? 'message').toLowerCase() })
    cancelCompose()
    emit('posted')
  } catch (err) {
    console.error('Failed to create post:', err)
    error.value = 'Failed to create post. Please try again.'
  } finally {
    submitting.value = false
  }
}

// Reset tag picker when closing
watch(showTagPicker, (open) => {
  if (!open) tagSearch.value = ''
})
</script>
